import type { APIBookingDraft, APIBookingDraftRoom } from '@/scopes/checkout/infrastructure/api/types';
import type { ServiceType, ServiceMapper } from '../../../domain/types';
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper';
import { isNil, values } from 'ramda';
import type {
  APIBookingDraftClothingService,
  APIBookingDraftClothingServiceOption,
} from '@/scopes/checkout/infrastructure/api/types/services/clothing';
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper';
import type {
  CheckoutClothingService,
  CheckoutClothingServiceOption,
  CheckoutClothingSelectedValue,
} from '@/scopes/checkout/domain/types/services/clothing';
import { GENDERS_ORDER, NO_GENDER, SIZES_ORDER } from '@/scopes/checkout/components/services/ClothingService/constants';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceOptionMapper?: ServiceOptionMapper;
  serviceParticipantsMapper?: ServiceParticipantsMapper;
  serviceType: ServiceType;
}

export const createClothingServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutClothingService> = (
  dependencies: Dependencies
) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceType,
  } = dependencies;

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftClothingService;

    if (!service) return null;

    const { aName, options } = service;

    const participants =
      bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, options),
            selectedValue:
              buildUniqOptionId(
                options
                  .filter(({ isSelected }) => isSelected)
                  .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))
              ) || undefined,
            availableOptions: options
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map((option) => buildUniqOptionId(option)),
          },
        }),
        {}
      ) ?? {};

    const anyParticipantHasSelectedValue = values(participants).some(({ selectedValue }) => selectedValue);
    return {
      id: aName,
      type: serviceType,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      isNoneOption: buildUniqOptionId(options.find(({ isNoneOption }) => isNoneOption)) ?? '',
      options: options.reduce(
        (acc2: Record<string, CheckoutClothingServiceOption>, option) => ({
          ...acc2,
          [buildUniqOptionId(option)]: {
            ...serviceOptionMapper.map(option, service, bookingDraft),
            id: buildUniqOptionId(option),
            gender: option.gender,
            size: option.size,
          },
        }),
        {}
      ),
      editingSelectedValues: options.reduce((acc3: Record<string, CheckoutClothingSelectedValue>, option) => {
        if (option.isSelected) {
          option.assignedParticipants.forEach((participantId) => {
            acc3[participantId] = {
              gender: option.gender,
              size: option.size,
              value: option.code,
              id: buildUniqOptionId(option),
            };
          });
        } else {
          bookingDraft.participants.forEach((participant) => {
            if (acc3[participant.id.toString()]) return;

            let genders = Array.from(
              new Set(
                Object.values(options)
                  .filter(({ gender }) => gender !== NO_GENDER)
                  .map(({ gender }) => gender)
              )
            );
            genders = genders.sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b));
            if (genders.length < 0) return;

            let sizes = Array.from(
              new Set(
                Object.values(options)
                  .filter(({ gender }) => gender === genders[0])
                  .map(({ size }) => size)
              )
            );
            sizes = sizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b));
            if (sizes.length < 0) return;

            let defaultOption = options.find(({ gender, size }) => gender === genders[0] && size === sizes[0]);
            if (!defaultOption) {
              defaultOption = options.find(({ isNoneOption }) => !isNoneOption);
            }
            acc3[participant.id.toString()] = {
              gender: defaultOption?.gender ?? '',
              size: defaultOption?.size ?? '',
              value: defaultOption?.code ?? '',
              id: buildUniqOptionId(defaultOption) ?? '',
            };
          });
        }
        return acc3;
      }, {}),
      sizes: options.reduce(
        (acc4: Record<string, { sizes: string[] }>, option) => ({
          ...acc4,
          [option.gender]: {
            sizes: options
              .filter(({ gender, isNoneOption }) => gender === option.gender && !isNoneOption)
              .map(({ size }) => size),
          },
        }),
        {}
      ),
    };
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map((participantId) => participantId.toString()),
    };
  }

  function mapSelection(service: CheckoutClothingService) {
    const { participants, options } = service;
    return Object.values(participants ?? {}).map(({ id, selectedValue }) => {
      const selectedOption = options[selectedValue ?? ''];
      return {
        participantId: id,
        value: selectedOption?.value ?? null,
      };
    });
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutClothingService,
    previousService: CheckoutClothingService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];

    if (!service) {
      return { ancillaryAddMapped, ancillaryRemoveMapped };
    }

    const participantsModified = Object.keys(service?.participants).reduce(
      (modified: { id: string; previousValue: string | null; newValue: string | null }[], id) => {
        const prevParticipant = previousService?.participants[id];
        const newParticipant = service?.participants[id];

        const prevSelectedValue = prevParticipant?.selectedValue;
        const newSelectedValue = newParticipant?.selectedValue;

        if (prevSelectedValue !== newSelectedValue) {
          modified.push({
            id,
            previousValue: prevSelectedValue,
            newValue: newSelectedValue,
          });
        }

        return modified;
      },
      []
    );

    participantsModified.forEach((participant) => {
      if (participant.newValue) {
        const option = service.options[participant.newValue];

        ancillaryAddMapped.push({
          id: option.value,
          name: option?.label || '',
          price: option?.price || 0,
          category: `Ancillary > Additional cost > ${service.type}`,
          quantity: 1,
          serviceType: service.type,
          extraProperties: {},
        });
      }
      if (participant.previousValue) {
        const option = service.options[participant.previousValue];

        ancillaryRemoveMapped.push({
          id: option.value,
          name: option?.label || '',
          price: option?.price || 0,
          category: `Ancillary > Additional cost > ${service.type}`,
          quantity: 1,
          serviceType: service.type,
          extraProperties: {},
        });
      }
    });

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  function buildUniqOptionId(option: APIBookingDraftClothingServiceOption | undefined) {
    if (!option) return '';
    return option.code.replaceAll(' ', '_').toLowerCase();
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
